import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import './builder.css';
import Navbar from '../Navbar/nbar';
import Footer from '../footer/Footer';

/* ═══════════════════════════════════════════════════════
   SOMFIN BUILDER v3 — Premium Financial Action Platform
   v2 audit fixes: fmt bug, perf, quiz completion,
   focus-visible, sticky progress, reset, celebration
   ═══════════════════════════════════════════════════════ */

const LS_KEY = 'smf_bld_v3';
const loadLS = () => { try { return JSON.parse(localStorage.getItem(LS_KEY)); } catch { return null; } };
const saveLS = d => { try { localStorage.setItem(LS_KEY, JSON.stringify(d)); } catch {} };
const clearLS = () => { try { localStorage.removeItem(LS_KEY); } catch {} };

const MODULES = [
  { id: 'emergency', label: 'Emergency Fund', status: 'active' },
  { id: 'health', label: 'Health Insurance', status: 'coming' },
  { id: 'term', label: 'Term Insurance', status: 'coming' },
  { id: 'invest', label: 'First Investment', status: 'coming' },
  { id: 'tax', label: 'Tax Planning', status: 'coming' },
];

const MOD_ICONS = {
  emergency: <><circle cx="12" cy="12" r="9" fill="none" strokeWidth="1.8"/><path d="M12 7v4M10 13h4v3h-4z" strokeWidth="1.5" strokeLinejoin="round"/></>,
  health: <><path d="M12 21s-8-7-8-12.5C4 5.46 6.46 3 9.5 3c1.56 0 2.5 1 2.5 1s.94-1 2.5-1C17.54 3 20 5.46 20 8.5 20 14 12 21 12 21z" fill="none" strokeWidth="1.8" strokeLinejoin="round"/></>,
  term: <><rect x="5" y="3" width="14" height="18" rx="2" fill="none" strokeWidth="1.8"/><line x1="9" y1="8" x2="15" y2="8" strokeWidth="1.5"/><line x1="9" y1="12" x2="15" y2="12" strokeWidth="1.5"/><line x1="9" y1="16" x2="12" y2="16" strokeWidth="1.5"/></>,
  invest: <><polyline points="3 17 9 11 13 15 21 7" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="17 7 21 7 21 11" fill="none" strokeWidth="2" strokeLinecap="round"/></>,
  tax: <><rect x="3" y="3" width="18" height="18" rx="3" fill="none" strokeWidth="1.8"/><line x1="3" y1="9" x2="21" y2="9" strokeWidth="1.5"/><line x1="9" y1="21" x2="9" y2="9" strokeWidth="1.5"/></>,
};

const CATS = [
  { id: 'rent', label: 'Rent / Housing', def: 15000 },
  { id: 'groceries', label: 'Groceries & Food', def: 8000 },
  { id: 'utilities', label: 'Utilities & Bills', def: 4000 },
  { id: 'transport', label: 'Transportation', def: 3000 },
  { id: 'insurance', label: 'Insurance Premiums', def: 2000 },
  { id: 'emi', label: 'Loan EMIs', def: 0 },
  { id: 'medical', label: 'Medical / Healthcare', def: 1000 },
  { id: 'education', label: 'Education', def: 0 },
  { id: 'other', label: 'Other Essentials', def: 2000 },
];

const PLACES = [
  { name: 'Savings Account', rate: '3\u20134%', access: 'Instant', risk: 'Zero', best: 'Often used for the first \u20B925K of a starter fund', clr: '#8E90A6', rec: false },
  { name: 'Liquid Mutual Fund', rate: '6\u20137%', access: '24 hrs (T+1)', risk: 'Very Low', best: 'Commonly chosen for the core portion of emergency savings', clr: '#1A8A63', rec: true },
  { name: 'Sweep-in FD', rate: '6\u20137.5%', access: 'Instant', risk: 'Zero', best: 'Available at select banks \u2014 combines FD rates with savings access', clr: '#4f297e', rec: false },
  { name: 'Short-term FD', rate: '6.5\u20137.5%', access: '1\u20133 days (penalty)', risk: 'Zero', best: 'Considered for surplus beyond 3 months of expenses', clr: '#A67C00', rec: false },
];

const MILES = [
  { t: 10000, lbl: '\u20B910,000', title: 'Starter Shield', desc: 'Covers a minor medical bill or appliance repair' },
  { t: 25000, lbl: '\u20B925,000', title: 'Safety Net', desc: 'Handles bike repair or emergency travel' },
  { t: 50000, lbl: '\u20B950,000', title: 'Stability Buffer', desc: 'Covers ~1 month expenses for most Indians' },
  { t: null, mo: 3, lbl: '3 Months', title: 'Security Cushion', desc: 'Handles job loss or major medical event' },
  { t: null, mo: 6, lbl: '6 Months', title: 'Financial Fortress', desc: 'Gold standard \u2014 peace of mind for any crisis' },
  { t: null, mo: 12, lbl: '12 Months', title: 'Ultimate Shield', desc: 'For freelancers or volatile industries' },
];

const QUIZ = [
  { q: 'New phone because yours is slow', a: false, r: 'Want, not need. Your phone still works.' },
  { q: 'Sudden hospitalisation \u2014 \u20B950,000 bill', a: true, r: 'Unexpected, urgent, and a survival need.' },
  { q: 'Friend\u2019s wedding gift \u2014 \u20B910,000', a: false, r: 'Expected social expense. Budget separately.' },
  { q: 'Job loss \u2014 need 3 months of rent', a: true, r: 'Unplanned income loss. This is what the fund is for.' },
  { q: 'AC repair in peak summer \u2014 \u20B98,000', a: true, r: 'Unexpected breakdown affecting health/comfort.' },
  { q: 'Annual car insurance renewal', a: false, r: 'Known, recurring expense. Budget for it.' },
  { q: 'Bike accident \u2014 \u20B915,000 repair', a: true, r: 'Unexpected, necessary for daily commute.' },
  { q: 'Sale on electronics \u2014 60% off', a: false, r: 'A sale is never an emergency.' },
];

const STEPS = [
  { n: '01', title: 'Why You Need This', sub: 'The India reality check' },
  { n: '02', title: 'Calculate Your Number', sub: 'Your personal target' },
  { n: '03', title: 'The Emergency Test', sub: 'What qualifies?' },
  { n: '04', title: 'Choose Your Tier', sub: 'Mini \u2192 Standard \u2192 Fortress' },
  { n: '05', title: 'Where to Park It', sub: 'Indian instruments compared' },
  { n: '06', title: 'Build Your Plan', sub: 'Monthly automation' },
  { n: '07', title: 'Track Progress', sub: 'Milestone checklist' },
];

/* FIX #1: Proper rupee formatter */
const fmt = n => '\u20B9' + Math.round(n).toLocaleString('en-IN');

const Builder = () => {
  /* FIX #2: load() inside useState initializer, not component body */
  const [initData] = useState(() => loadLS());
  const [mod, setMod] = useState('emergency');
  const [step, setStep] = useState(initData?.step || 0);
  const [exp, setExp] = useState(() => {
    if (initData?.exp) return initData.exp;
    const o = {}; CATS.forEach(c => { o[c.id] = c.def; }); return o;
  });
  const [months, setMo] = useState(initData?.months || 6);
  const [qi, setQI] = useState(initData?.qi || 0);
  const [qa, setQA] = useState(initData?.qa || {});
  const [showScore, setShowScore] = useState(false);
  const [curSav, setCS] = useState(initData?.curSav || 0);
  const [moSave, setMS] = useState(initData?.moSave || 5000);
  const [done, setDone] = useState(() => new Set(initData?.done || []));
  const topRef = useRef(null);
  const fbRef = useRef(null);

  const totalMo = useMemo(() => Object.values(exp).reduce((a, b) => a + (b || 0), 0), [exp]);
  const target = useMemo(() => totalMo * months, [totalMo, months]);
  const remain = useMemo(() => Math.max(target - curSav, 0), [target, curSav]);
  const monthsLeft = useMemo(() => moSave > 0 ? Math.ceil(remain / moSave) : 999, [remain, moSave]);
  const pct = Math.round(((step + 1) / STEPS.length) * 100);
  const goalPct = target > 0 ? Math.min(Math.round((curSav / target) * 100), 100) : 0;

  const quizScore = Object.entries(qa).filter(([i, v]) => v === QUIZ[+i].a).length;

  useEffect(() => { saveLS({ step, exp, months, curSav, moSave, done: [...done], qa, qi }); }, [step, exp, months, curSav, moSave, done, qa, qi]);
  useEffect(() => { topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [step]);

  /* FIX #13: Focus feedback after quiz answer */
  useEffect(() => { if (qa[qi] !== undefined && fbRef.current) fbRef.current.focus(); }, [qa, qi]);

  const updExp = useCallback((id, v) => setExp(p => ({ ...p, [id]: Math.max(0, parseInt(v) || 0) })), []);
  const go = s => setStep(Math.max(0, Math.min(s, STEPS.length - 1)));
  const answer = v => setQA(p => ({ ...p, [qi]: v }));
  const toggleDone = i => { setDone(p => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; }); };

  /* FIX #5: Quiz reset */
  const resetQuiz = () => { setQA({}); setQI(0); setShowScore(false); };

  /* FIX #6: Full reset */
  const resetAll = () => {
    if (!window.confirm('Reset all Builder data? This clears your expenses, savings, and milestones.')) return;
    clearLS();
    const o = {}; CATS.forEach(c => { o[c.id] = c.def; });
    setStep(0); setExp(o); setMo(6); setQA({}); setQI(0); setShowScore(false); setCS(0); setMS(5000); setDone(new Set());
  };



  return (
    <div className="bld">
      <Navbar />

      {/* HERO */}
      <section className="bld__hero" ref={topRef}>
        <svg className="bld__hero-bg" viewBox="0 0 1400 450" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="bldG1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4f297e" stopOpacity="0.07"/><stop offset="100%" stopColor="#1A8A63" stopOpacity="0.04"/></linearGradient>
            <linearGradient id="bldG2" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#4f297e" stopOpacity="0.05"/><stop offset="100%" stopColor="#1A8A63" stopOpacity="0.08"/></linearGradient>
            <linearGradient id="bldG3" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#4f297e" stopOpacity="0.06"/><stop offset="100%" stopColor="transparent"/></linearGradient>
          </defs>

          {/* Layered protection arcs — left cluster */}
          <g opacity="0.5">
            <path d="M180 225 A120 120 0 0 1 300 105" fill="none" stroke="url(#bldG1)" strokeWidth="1"><animate attributeName="stroke-dashoffset" values="0;40" dur="12s" repeatCount="indefinite"/></path>
            <path d="M160 225 A140 140 0 0 1 300 85" fill="none" stroke="rgba(79,41,126,0.04)" strokeWidth="0.8" strokeDasharray="3 6"/>
            <path d="M140 225 A160 160 0 0 1 300 65" fill="none" stroke="rgba(79,41,126,0.03)" strokeWidth="0.6" strokeDasharray="2 8"/>
          </g>

          {/* Layered protection arcs — right cluster */}
          <g opacity="0.5">
            <path d="M1100 225 A120 120 0 0 0 1220 345" fill="none" stroke="url(#bldG2)" strokeWidth="1"><animate attributeName="stroke-dashoffset" values="40;0" dur="14s" repeatCount="indefinite"/></path>
            <path d="M1120 225 A140 140 0 0 0 1260 365" fill="none" stroke="rgba(26,138,99,0.04)" strokeWidth="0.8" strokeDasharray="3 6"/>
            <path d="M1140 225 A160 160 0 0 0 1300 385" fill="none" stroke="rgba(26,138,99,0.03)" strokeWidth="0.6" strokeDasharray="2 8"/>
          </g>

          {/* Geometric mesh — hexagonal grid fragments */}
          <g opacity="0.04" stroke="#4f297e" strokeWidth="0.8" fill="none">
            <polygon points="100,180 130,163 160,180 160,214 130,231 100,214"/>
            <polygon points="160,180 190,163 220,180 220,214 190,231 160,214"/>
            <polygon points="130,231 160,214 190,231 190,265 160,282 130,265"/>
            <polygon points="1180,140 1210,123 1240,140 1240,174 1210,191 1180,174"/>
            <polygon points="1240,140 1270,123 1300,140 1300,174 1270,191 1240,174"/>
            <polygon points="1210,191 1240,174 1270,191 1270,225 1240,242 1210,225"/>
          </g>

          {/* Node constellation — financial safety net */}
          <g opacity="0.07">
            {/* Left network */}
            <circle cx="220" cy="120" r="4" fill="#4f297e"/><circle cx="320" cy="80" r="3" fill="#4f297e"/><circle cx="280" cy="180" r="5" fill="#1A8A63"/>
            <circle cx="180" cy="280" r="3" fill="#4f297e"/><circle cx="350" cy="250" r="4" fill="#1A8A63"/>
            <line x1="220" y1="120" x2="320" y2="80" stroke="#4f297e" strokeWidth="0.8"/>
            <line x1="220" y1="120" x2="280" y2="180" stroke="#4f297e" strokeWidth="0.8"/>
            <line x1="280" y1="180" x2="350" y2="250" stroke="#1A8A63" strokeWidth="0.8"/>
            <line x1="180" y1="280" x2="280" y2="180" stroke="#4f297e" strokeWidth="0.6"/>
            <line x1="320" y1="80" x2="280" y2="180" stroke="#4f297e" strokeWidth="0.6"/>

            {/* Right network */}
            <circle cx="1080" cy="100" r="4" fill="#1A8A63"/><circle cx="1180" cy="150" r="3" fill="#4f297e"/><circle cx="1130" cy="250" r="5" fill="#4f297e"/>
            <circle cx="1250" cy="300" r="3" fill="#1A8A63"/><circle cx="1050" cy="320" r="4" fill="#1A8A63"/>
            <line x1="1080" y1="100" x2="1180" y2="150" stroke="#1A8A63" strokeWidth="0.8"/>
            <line x1="1180" y1="150" x2="1130" y2="250" stroke="#4f297e" strokeWidth="0.8"/>
            <line x1="1130" y1="250" x2="1250" y2="300" stroke="#1A8A63" strokeWidth="0.8"/>
            <line x1="1130" y1="250" x2="1050" y2="320" stroke="#4f297e" strokeWidth="0.6"/>
            <line x1="1080" y1="100" x2="1130" y2="250" stroke="#4f297e" strokeWidth="0.6"/>
          </g>

          {/* Central abstract — layered concentric rings (protection/foundation) */}
          <g transform="translate(700,225)" opacity="0.06">
            <circle r="140" fill="none" stroke="#4f297e" strokeWidth="0.6" strokeDasharray="4 12"><animateTransform attributeName="transform" type="rotate" values="0;360" dur="90s" repeatCount="indefinite"/></circle>
            <circle r="110" fill="none" stroke="#1A8A63" strokeWidth="0.8" strokeDasharray="6 8"><animateTransform attributeName="transform" type="rotate" values="360;0" dur="70s" repeatCount="indefinite"/></circle>
            <circle r="80" fill="none" stroke="#4f297e" strokeWidth="1" strokeDasharray="8 6"><animateTransform attributeName="transform" type="rotate" values="0;360" dur="50s" repeatCount="indefinite"/></circle>
            {/* Inner diamond */}
            <polygon points="0,-45 35,0 0,45 -35,0" fill="none" stroke="#4f297e" strokeWidth="1.2"><animateTransform attributeName="transform" type="rotate" values="0;360" dur="120s" repeatCount="indefinite"/></polygon>
          </g>

          {/* Flowing data curves */}
          <path d="M0 380Q200 280 400 320T800 200T1200 280T1400 220" fill="none" stroke="rgba(79,41,126,0.035)" strokeWidth="1.5">
            <animate attributeName="d" values="M0 380Q200 280 400 320T800 200T1200 280T1400 220;M0 360Q200 300 400 290T800 230T1200 260T1400 240;M0 380Q200 280 400 320T800 200T1200 280T1400 220" dur="20s" repeatCount="indefinite"/>
          </path>
          <path d="M0 350Q350 200 700 270T1400 180" fill="none" stroke="rgba(26,138,99,0.025)" strokeWidth="1">
            <animate attributeName="d" values="M0 350Q350 200 700 270T1400 180;M0 330Q350 230 700 240T1400 200;M0 350Q350 200 700 270T1400 180" dur="16s" repeatCount="indefinite"/>
          </path>

          {/* Subtle tick marks — like a financial chart axis */}
          <g opacity="0.03" stroke="#4f297e" strokeWidth="0.8">
            <line x1="50" y1="400" x2="50" y2="390"/><line x1="100" y1="400" x2="100" y2="393"/>
            <line x1="150" y1="400" x2="150" y2="390"/><line x1="200" y1="400" x2="200" y2="393"/>
            <line x1="250" y1="400" x2="250" y2="390"/><line x1="300" y1="400" x2="300" y2="393"/>
            <line x1="1100" y1="400" x2="1100" y2="390"/><line x1="1150" y1="400" x2="1150" y2="393"/>
            <line x1="1200" y1="400" x2="1200" y2="390"/><line x1="1250" y1="400" x2="1250" y2="393"/>
            <line x1="1300" y1="400" x2="1300" y2="390"/><line x1="1350" y1="400" x2="1350" y2="393"/>
          </g>

          {/* Floating micro-particles */}
          <circle cx="450" cy="90" r="2" fill="#4f297e" opacity="0.05"><animate attributeName="cy" values="90;70;90" dur="8s" repeatCount="indefinite"/></circle>
          <circle cx="950" cy="350" r="2.5" fill="#1A8A63" opacity="0.04"><animate attributeName="cy" values="350;330;350" dur="10s" repeatCount="indefinite"/></circle>
          <circle cx="600" cy="370" r="1.5" fill="#4f297e" opacity="0.04"><animate attributeName="cy" values="370;355;370" dur="6s" repeatCount="indefinite"/></circle>
          <circle cx="800" cy="60" r="2" fill="#1A8A63" opacity="0.05"><animate attributeName="cy" values="60;45;60" dur="9s" repeatCount="indefinite"/></circle>
        </svg>
        <div className="bld__hero-body">
          <span className="bld__pill">SOMFIN Builder</span>
          <div className="bld__hero-h">Build Your Financial Foundation.</div>
          <p className="bld__hero-p">Step-by-step guided builders for every critical financial action. Not advice. Execution.</p>
        </div>
      </section>

      {/* DISCLAIMER BANNER */}
      <div className="bld__disclaimer-bar">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="bld__disclaimer-ico"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <span className="bld__disclaimer-txt">This tool is for financial literacy and learning purposes only. It does not constitute financial advice, investment recommendation, or endorsement of any product or service. Please consult a qualified financial advisor before making any financial decisions.</span>
      </div>

      {/* MODULE BAR */}
      <div className="bld__modbar">
        <div className="bld__modbar-inner">
          {MODULES.map(m => (
            <button key={m.id} className={`bld__mod${mod === m.id ? ' bld__mod--on' : ''}${m.status === 'coming' ? ' bld__mod--soon' : ''}`}
              onClick={() => m.status === 'active' && setMod(m.id)} disabled={m.status === 'coming'} aria-label={m.label}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeLinecap="round" className="bld__mod-ico">{MOD_ICONS[m.id]}</svg>
              <span className="bld__mod-lbl">{m.label}</span>
              {m.status === 'coming' && <span className="bld__mod-soon">Soon</span>}
            </button>
          ))}
        </div>
      </div>

      {/* STEP INDICATOR BAR — sticky below navbar */}
      <div className="bld__stepbar">
        <div className="bld__stepbar-inner">
          <div className="bld__stepbar-left">
            <span className="bld__stepbar-num">{STEPS[step].n}</span>
            <div className="bld__stepbar-text">
              <span className="bld__stepbar-title">{STEPS[step].title}</span>
              <span className="bld__stepbar-sub">{STEPS[step].sub}</span>
            </div>
          </div>
          <div className="bld__stepbar-right">
            <div className="bld__stepbar-dots">
              {STEPS.map((_, i) => (
                <button key={i} className={`bld__stepbar-dot${i === step ? ' bld__stepbar-dot--on' : ''}${i < step ? ' bld__stepbar-dot--done' : ''}`}
                  onClick={() => go(i)} aria-label={`Go to step ${i + 1}`} />
              ))}
            </div>
            <span className="bld__stepbar-pct">{pct}%</span>
          </div>
        </div>
        <div className="bld__stepbar-track"><div className="bld__stepbar-fill" style={{ width: `${pct}%` }} /></div>
      </div>

      {/* BODY */}
      {mod === 'emergency' && (
        <div className="bld__body">
          <div className="bld__wrap">

            {/* SIDEBAR — vertical step rail */}
            <nav className="bld__side" aria-label="Builder steps">
              <div className="bld__side-label">Emergency Fund</div>
              <div className="bld__side-rail">
                {STEPS.map((s, i) => (
                  <button key={i} className={`bld__side-btn${step === i ? ' bld__side-btn--on' : ''}${i < step ? ' bld__side-btn--done' : ''}`}
                    onClick={() => go(i)} aria-current={step === i ? 'step' : undefined} aria-label={`Step ${s.n}: ${s.title}`}>
                    {step === i && <span className="bld__side-indicator" />}
                    <span className="bld__side-num">{i < step ? '\u2713' : s.n}</span>
                    <div className="bld__side-meta"><span className="bld__side-title">{s.title}</span><span className="bld__side-sub">{s.sub}</span></div>
                  </button>
                ))}
              </div>
              <div className="bld__side-foot">
                <div className="bld__side-note">For learning & literacy only {'\u2014'} not financial advice or recommendation.</div>
                <button className="bld__side-reset" onClick={resetAll} aria-label="Reset all data">Reset Data</button>
              </div>
            </nav>

            {/* PANELS */}
            <div className="bld__main">

              {/* STEP 1 */}
              <div className={`bld__card${step === 0 ? ' bld__card--on' : ''}`} data-step="why-you-need-this">
                <div className="bld__card-head"><span className="bld__card-num">01</span><div><div className="bld__card-h">Why You Need This</div><p className="bld__card-sub">The India reality check</p></div></div>
                <div className="bld__callout"><div className="bld__callout-bar" /><div><div className="bld__callout-h">The India Reality</div><p>52% of Indians cannot raise emergency funds within a month. Of those who can, nearly half rely on family and friends {'\u2014'} not savings. Only 23% use their own saved money in a crisis.</p><p className="bld__cite">Source: World Bank Global Findex Database</p></div></div>
                <div className="bld__stats">
                  <div className="bld__stat"><div className="bld__stat-n bld__c-red">52%</div><div className="bld__stat-l">can{'\u2019'}t raise emergency funds in a month</div></div>
                  <div className="bld__stat"><div className="bld__stat-n bld__c-gold">48%</div><div className="bld__stat-l">rely on family & friends in a crisis</div></div>
                  <div className="bld__stat"><div className="bld__stat-n bld__c-green">23%</div><div className="bld__stat-l">actually use their own savings</div></div>
                </div>
                <p className="bld__prose">An emergency fund is money set aside for unplanned, urgent expenses {'\u2014'} job loss, medical bills, vehicle breakdown, home repairs. It is the difference between an inconvenience and a financial crisis.</p>
                <p className="bld__prose">Without one, you are forced into credit card debt at 36{'\u2013'}42% APR, personal loans at 14%+, or borrowing from family. Research shows each point of better financial behaviour increases your odds of having an emergency fund by 21.9%.</p>
                <div className="bld__vs">
                  <div className="bld__vs-card bld__vs--bad">
                    <svg viewBox="0 0 40 40" width="28" height="28" className="bld__vs-ico"><circle cx="20" cy="20" r="16" fill="none" stroke="#C0392B" strokeWidth="2" /><line x1="14" y1="14" x2="26" y2="26" stroke="#C0392B" strokeWidth="2.5" strokeLinecap="round" /><line x1="26" y1="14" x2="14" y2="26" stroke="#C0392B" strokeWidth="2.5" strokeLinecap="round" /></svg>
                    <div className="bld__vs-title">Without Emergency Fund</div>
                    <p>Credit card debt at 36{'\u2013'}42% APR. Personal loans at 14%+. Selling investments at a loss. Borrowing from family. Financial stress affecting health and work.</p>
                  </div>
                  <div className="bld__vs-card bld__vs--good">
                    <svg viewBox="0 0 40 40" width="28" height="28" className="bld__vs-ico"><circle cx="20" cy="20" r="16" fill="none" stroke="#1A8A63" strokeWidth="2" /><polyline points="13 20 18 25 27 15" fill="none" stroke="#1A8A63" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    <div className="bld__vs-title">With Emergency Fund</div>
                    <p>Pay from your own reserves. Zero interest. Zero debt. SIPs continue uninterrupted. Peace of mind knowing you are protected against any crisis.</p>
                  </div>
                </div>
                <button className="bld__cta" onClick={() => go(1)}>Next: Calculate Your Number {'\u2192'}</button>
              </div>

              {/* STEP 2 */}
              <div className={`bld__card${step === 1 ? ' bld__card--on' : ''}`} data-step="calculate-your-number">
                <div className="bld__card-head"><span className="bld__card-num">02</span><div><div className="bld__card-h">Calculate Your Number</div><p className="bld__card-sub">Your personal emergency fund target</p></div></div>
                <p className="bld__prose">Enter your monthly essential expenses {'\u2014'} costs you must pay every month to survive. Do not include wants like dining out, shopping, or entertainment.</p>
                <div className="bld__exp-grid">
                  {CATS.map(c => (
                    <div key={c.id} className="bld__exp-row">
                      <label className="bld__exp-lbl" htmlFor={`e-${c.id}`}>{c.label}</label>
                      <div className="bld__inp-wrap"><span className="bld__inp-pre">{'\u20B9'}</span><input id={`e-${c.id}`} type="number" className="bld__inp" value={exp[c.id]} onChange={e => updExp(c.id, e.target.value)} min="0" max="500000" step="500" aria-label={`${c.label} amount`} /></div>
                    </div>
                  ))}
                </div>
                <div className="bld__result">
                  {totalMo === 0 && <div className="bld__nudge">Enter your expenses above to calculate your target.</div>}
                  <div className="bld__res-row"><span>Monthly Essential Expenses</span><span className="bld__res-val">{fmt(totalMo)}</span></div>
                  <div className="bld__res-div" />
                  <div className="bld__res-row"><span>Coverage Period</span>
                    <div className="bld__mo-btns">{[3, 6, 9, 12].map(m => (
                      <button key={m} className={`bld__mo-btn${months === m ? ' bld__mo-btn--on' : ''}`} onClick={() => setMo(m)} aria-label={`${m} months`} aria-pressed={months === m}>{m} mo</button>
                    ))}</div>
                  </div>
                  <div className="bld__res-div" />
                  <div className="bld__res-row bld__res-row--big"><span>Your Emergency Fund Target</span><span className="bld__res-big">{fmt(target)}</span></div>
                </div>
                <div className="bld__tiers">
                  <div className="bld__tier"><span className="bld__tier-tag">Minimum</span><span className="bld__tier-val">{fmt(totalMo * 3)}</span><span className="bld__tier-desc">3 mo {'\u2014'} dual income, stable job</span></div>
                  <div className="bld__tier bld__tier--hl"><span className="bld__tier-tag">Recommended</span><span className="bld__tier-val">{fmt(totalMo * 6)}</span><span className="bld__tier-desc">6 mo {'\u2014'} single income, salaried</span></div>
                  <div className="bld__tier"><span className="bld__tier-tag">Conservative</span><span className="bld__tier-val">{fmt(totalMo * 12)}</span><span className="bld__tier-desc">12 mo {'\u2014'} freelancer, volatile income</span></div>
                </div>
                <button className="bld__cta" onClick={() => go(2)}>Next: The Emergency Test {'\u2192'}</button>
              </div>

              {/* STEP 3: Quiz */}
              <div className={`bld__card${step === 2 ? ' bld__card--on' : ''}`} data-step="emergency-test">
                <div className="bld__card-head"><span className="bld__card-num">03</span><div><div className="bld__card-h">The Emergency Test</div><p className="bld__card-sub">Before touching your fund, ask 3 questions</p></div></div>
                <div className="bld__rules">
                  <div className="bld__rule"><span className="bld__rule-n">1</span><span>Is it <strong>unexpected</strong>?</span></div>
                  <div className="bld__rule"><span className="bld__rule-n">2</span><span>Is it a <strong>need</strong>?</span></div>
                  <div className="bld__rule"><span className="bld__rule-n">3</span><span>Is it <strong>urgent</strong>?</span></div>
                </div>

                {/* Quiz: showScore controls when score ring appears */}
                {showScore ? (
                  <div className="bld__q-done">
                    <div className="bld__q-score-ring">
                      <svg viewBox="0 0 80 80" width="72" height="72"><circle cx="40" cy="40" r="32" fill="none" stroke="#EBEBF0" strokeWidth="6" /><circle cx="40" cy="40" r="32" fill="none" stroke={quizScore >= 7 ? '#1A8A63' : quizScore >= 5 ? '#A67C00' : '#C0392B'} strokeWidth="6" strokeDasharray={2 * Math.PI * 32} strokeDashoffset={2 * Math.PI * 32 * (1 - quizScore / QUIZ.length)} strokeLinecap="round" transform="rotate(-90 40 40)" /></svg>
                      <span className="bld__q-score-num">{quizScore}/{QUIZ.length}</span>
                    </div>
                    <div className="bld__q-score-label">{quizScore === 8 ? 'Perfect! You know your emergencies.' : quizScore >= 6 ? 'Great job! Strong instincts.' : quizScore >= 4 ? 'Good start. Review the ones you missed.' : 'Worth reviewing \u2014 this framework will help.'}</div>
                    <button className="bld__q-retry" onClick={resetQuiz}>Retake Quiz</button>
                  </div>
                ) : (
                  <>
                    <div className="bld__q-counter">{qi + 1} / {QUIZ.length}</div>
                    <div className="bld__q-box">
                      <div className="bld__q-text">{QUIZ[qi].q}</div>
                      {qa[qi] === undefined ? (
                        <div className="bld__q-btns">
                          <button className="bld__q-yes" onClick={() => answer(true)} aria-label="Yes, emergency">Yes {'\u2014'} Emergency</button>
                          <button className="bld__q-no" onClick={() => answer(false)} aria-label="No, not emergency">No {'\u2014'} Not Emergency</button>
                        </div>
                      ) : (
                        <div ref={fbRef} tabIndex={-1} className={`bld__q-fb${qa[qi] === QUIZ[qi].a ? ' bld__q-fb--ok' : ' bld__q-fb--wrong'}`}>
                          <span className="bld__q-verdict">{qa[qi] === QUIZ[qi].a ? '\u2713 Correct' : '\u2717 Incorrect'}</span>
                          <p>{QUIZ[qi].r}</p>
                          {qi < QUIZ.length - 1 && <button className="bld__q-next" onClick={() => setQI(p => p + 1)}>Next Scenario {'\u2192'}</button>}
                          {qi === QUIZ.length - 1 && <button className="bld__q-next" onClick={() => setShowScore(true)}>See Results {'\u2192'}</button>}
                        </div>
                      )}
                    </div>
                    <div className="bld__q-dots">{QUIZ.map((_, i) => (<span key={i} className={`bld__q-dot${i === qi ? ' bld__q-dot--on' : ''}${qa[i] !== undefined ? (qa[i] === QUIZ[i].a ? ' bld__q-dot--ok' : ' bld__q-dot--bad') : ''}`} />))}</div>
                  </>
                )}

                <button className="bld__cta" onClick={() => go(3)}>Next: Choose Your Tier {'\u2192'}</button>
              </div>

              {/* STEP 4 */}
              <div className={`bld__card${step === 3 ? ' bld__card--on' : ''}`} data-step="choose-your-tier">
                <div className="bld__card-head"><span className="bld__card-num">04</span><div><div className="bld__card-h">Choose Your Tier</div><p className="bld__card-sub">Each tier unlocks a new level of protection</p></div></div>
                <p className="bld__prose">Your fund doesn{'\u2019'}t have to be built overnight. Think of it as climbing a ladder {'\u2014'} each rung gives you more stability.</p>
                <div className="bld__tline">
                  {MILES.map((m, i) => {
                    const val = m.t || (totalMo * (m.mo || 3));
                    return (
                      <div key={i} className={`bld__tl${m.mo ? ' bld__tl--mo' : ''}`}>
                        <div className="bld__tl-rail"><div className="bld__tl-dot" /></div>
                        <div className="bld__tl-body">
                          <div className="bld__tl-badge">{m.lbl}</div>
                          <div className="bld__tl-title">{m.title}</div>
                          <p className="bld__tl-desc">{m.desc}</p>
                          <div className="bld__tl-amt">{fmt(val)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button className="bld__cta" onClick={() => go(4)}>Next: Where to Park It {'\u2192'}</button>
              </div>

              {/* STEP 5 */}
              <div className={`bld__card${step === 4 ? ' bld__card--on' : ''}`} data-step="where-to-park">
                <div className="bld__card-head"><span className="bld__card-num">05</span><div><div className="bld__card-h">Where to Park Your Money</div><p className="bld__card-sub">Common instruments used for emergency funds in India</p></div></div>
                <p className="bld__prose">Emergency funds are typically kept in instruments that offer quick access {'\u2014'} ideally within 24{'\u2013'}48 hours. Below is an overview of options commonly considered, along with their general characteristics:</p>
                <div className="bld__pl-grid">
                  {PLACES.map((p, i) => (
                    <div key={i} className={`bld__pl${p.rec ? ' bld__pl--rec' : ''}`} style={{ borderLeftColor: p.clr }}>
                      {p.rec && <span className="bld__pl-badge">Most Common Choice</span>}
                      <div className="bld__pl-name">{p.name}</div>
                      <div className="bld__pl-rows">
                        <div><span className="bld__pl-k">Returns</span><span className="bld__pl-v">{p.rate}</span></div>
                        <div><span className="bld__pl-k">Access</span><span className="bld__pl-v">{p.access}</span></div>
                        <div><span className="bld__pl-k">Risk</span><span className="bld__pl-v">{p.risk}</span></div>
                      </div>
                      <p className="bld__pl-best">{p.best}</p>
                    </div>
                  ))}
                </div>
                <div className="bld__callout"><div className="bld__callout-bar" /><div><div className="bld__callout-h">How People Typically Structure This</div><p>A common approach is to keep {'\u20B9'}10K{'\u2013'}25K in a savings account for same-day access, with the remainder in a liquid mutual fund for higher returns with T+1 redemption. Some banks also offer sweep-in FDs that combine FD rates with savings-like access. The right mix depends on your personal situation {'\u2014'} consider speaking with a financial advisor.</p></div></div>
                <div className="bld__disc">This section is for informational and educational purposes only. It is not a recommendation, endorsement, or solicitation to invest in any specific financial product. Returns and features mentioned are indicative and subject to change. Please consult a SEBI-registered financial advisor before making any investment decisions.</div>
                <button className="bld__cta" onClick={() => go(5)}>Next: Build Your Plan {'\u2192'}</button>
              </div>

              {/* STEP 6 */}
              <div className={`bld__card${step === 5 ? ' bld__card--on' : ''}`} data-step="build-your-plan">
                <div className="bld__card-head"><span className="bld__card-num">06</span><div><div className="bld__card-h">Build Your Savings Plan</div><p className="bld__card-sub">Automate on salary day {'\u2014'} treat it like a bill</p></div></div>
                <div className="bld__plan-fields">
                  <div className="bld__plan-f"><label className="bld__plan-lbl" htmlFor="cs">Current Emergency Savings</label><div className="bld__inp-wrap"><span className="bld__inp-pre">{'\u20B9'}</span><input id="cs" type="number" className="bld__inp" value={curSav} onChange={e => setCS(Math.max(0, +e.target.value || 0))} min="0" aria-label="Current savings" /></div></div>
                  <div className="bld__plan-f"><label className="bld__plan-lbl" htmlFor="ms">Monthly Savings Amount</label><div className="bld__inp-wrap"><span className="bld__inp-pre">{'\u20B9'}</span><input id="ms" type="number" className="bld__inp" value={moSave} onChange={e => setMS(Math.max(0, +e.target.value || 0))} min="0" aria-label="Monthly savings" /></div><input type="range" className="bld__slider" min={0} max={50000} step={500} value={moSave} onChange={e => setMS(+e.target.value)} aria-label="Monthly savings slider" /></div>
                </div>

                {/* FIX #8: Inline progress bar toward goal */}
                <div className="bld__goal-bar">
                  <div className="bld__goal-track"><div className="bld__goal-fill" style={{ width: `${goalPct}%` }} /></div>
                  <span className="bld__goal-pct">{goalPct}% of target</span>
                </div>

                <div className="bld__plan-out">
                  <div className="bld__plan-m"><div className="bld__plan-v">{fmt(target)}</div><div className="bld__plan-l">Target ({months}mo)</div></div>
                  <div className="bld__plan-m"><div className="bld__plan-v">{fmt(curSav)}</div><div className="bld__plan-l">Saved So Far</div></div>
                  <div className="bld__plan-m"><div className="bld__plan-v">{fmt(remain)}</div><div className="bld__plan-l">Remaining</div></div>
                  <div className="bld__plan-m bld__plan-m--hl"><div className="bld__plan-v">{monthsLeft > 900 ? '\u2014' : monthsLeft}</div><div className="bld__plan-l">{monthsLeft > 900 ? 'Set amount' : 'Months to Goal'}</div></div>
                </div>
                <div className="bld__tips">
                  <div className="bld__tip"><strong>Automate:</strong> Standing instruction to transfer {fmt(moSave)} on salary day.</div>
                  <div className="bld__tip"><strong>Windfall rule:</strong> Put 50% of any bonus, tax refund, or gift into this fund.</div>
                  <div className="bld__tip"><strong>Challenge:</strong> Try one month of zero non-essential spending. It accelerates dramatically.</div>
                </div>
                <button className="bld__cta" onClick={() => go(6)}>Next: Track Progress {'\u2192'}</button>
              </div>

              {/* STEP 7 */}
              <div className={`bld__card${step === 6 ? ' bld__card--on' : ''}`} data-step="track-progress">
                <div className="bld__card-head"><span className="bld__card-num">07</span><div><div className="bld__card-h">Track Your Milestones</div><p className="bld__card-sub">Check off as you achieve {'\u2014'} saved automatically</p></div></div>

                <div className="bld__ring-wrap">
                  <svg viewBox="0 0 100 100" width="90" height="90"><circle cx="50" cy="50" r="40" fill="none" stroke="#EBEBF0" strokeWidth="7" /><circle cx="50" cy="50" r="40" fill="none" stroke="#1A8A63" strokeWidth="7" strokeDasharray={2 * Math.PI * 40} strokeDashoffset={2 * Math.PI * 40 * (1 - done.size / MILES.length)} strokeLinecap="round" transform="rotate(-90 50 50)" style={{ transition: 'stroke-dashoffset 0.6s ease' }} /></svg>
                  <div className="bld__ring-info"><span className="bld__ring-pct">{Math.round(done.size / MILES.length * 100)}%</span><span className="bld__ring-cnt">{done.size}/{MILES.length}</span></div>
                </div>

                {/* FIX #9: Completion celebration */}
                {done.size === MILES.length && (
                  <div className="bld__celebrate">
                    <div className="bld__celebrate-h">All Milestones Complete!</div>
                    <p>You{'\u2019'}ve built a complete emergency fund. Your financial foundation is solid. Consider printing this plan for your records.</p>
                    <button className="bld__celebrate-print" onClick={() => window.print()} aria-label="Print your plan">Print Your Plan</button>
                  </div>
                )}

                <div className="bld__checks">
                  {MILES.map((m, i) => (
                    <button key={i} className={`bld__chk${done.has(i) ? ' bld__chk--on' : ''}`} onClick={() => toggleDone(i)} role="checkbox" aria-checked={done.has(i)} aria-label={`${m.title}: ${m.desc}`}>
                      <span className="bld__chk-box">{done.has(i) ? '\u2713' : ''}</span>
                      <div className="bld__chk-info"><div className="bld__chk-badge">{m.lbl}</div><div className="bld__chk-title">{m.title}</div><div className="bld__chk-desc">{m.desc}</div></div>
                    </button>
                  ))}
                </div>
                <div className="bld__disc">This tool is for financial literacy and learning purposes only. It is not financial advice, investment recommendation, or endorsement of any financial product. Consult a SEBI-registered advisor before making financial decisions.</div>

                {/* DOWNLOAD PLANNING SHEET */}
                <div className="bld__dl-section">
                  <div className="bld__dl-head">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#4f297e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="bld__dl-ico"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    <div>
                      <div className="bld__dl-title">Download Your Planning Sheet</div>
                      <p className="bld__dl-desc">A professional Excel workbook with formulas, colour-coded inputs, and a 24-month tracker. Edit the blue cells {'\u2014'} everything recalculates automatically.</p>
                    </div>
                  </div>
                  <div className="bld__dl-preview">
                    <div className="bld__dl-preview-h">4 Sheets Inside:</div>
                    <div className="bld__dl-items">
                      <div className="bld__dl-item"><span className="bld__dl-num">1</span><span>Emergency Fund Planner {'\u2014'} expenses, target, 3-question test</span></div>
                      <div className="bld__dl-item"><span className="bld__dl-num">2</span><span>Placement & Action Plan {'\u2014'} where to park + 8 action steps</span></div>
                      <div className="bld__dl-item"><span className="bld__dl-num">3</span><span>Monthly Tracker {'\u2014'} 24-month log with auto-calculated progress</span></div>
                      <div className="bld__dl-item"><span className="bld__dl-num">4</span><span>Quick Reference {'\u2014'} golden rules, milestones, India stats</span></div>
                    </div>
                  </div>
                  <a className="bld__dl-btn" href="https://docs.google.com/spreadsheets/d/1RyotFixvZYgveJms-TZY5hLo1OsMNJ6v/edit?usp=drive_link&ouid=102936162549828221024&rtpof=true&sd=true" download aria-label="Download emergency fund planning sheet">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download Excel Planning Sheet
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* STICKY BOTTOM NAV (mobile) */}
      {mod === 'emergency' && (
        <div className="bld__bot">
          <button className="bld__bot-prev" onClick={() => go(step - 1)} disabled={step === 0} aria-label="Previous step">{'\u2190'} Prev</button>
          <span className="bld__bot-lbl">{step + 1}/{STEPS.length}</span>
          <button className="bld__bot-next" onClick={() => go(step + 1)} disabled={step === STEPS.length - 1} aria-label="Next step">Next {'\u2192'}</button>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Builder;